import { Typography, List } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { UPDATE_USER } from '../graphql';
import GeneralListItem from '../../common/lists/generalListItem';
import OvForm from '../../common/wrappers/ovForm';
import SimpleInput from '../../common/inputs/simpleInput';
import { pmamSourceOfFunds } from '../resources';

export interface UserState {
  sourceOfFunds?: string[],
  incompleteFields?: string[],
}

interface Props {
  user: UserState,
  updateUserState: (att: UserState) => void,
  onContinue?: () => void,
  cached?: boolean,
  title?: string,
  subTitle?: string,
  analyticsEvent?: string,
}

const SourceOfFunds = ({
  user, updateUserState, ...props
}: Props): JSX.Element => {
  const { t } = useTranslation(['base', 'user']);
  const [loading, setLoading] = useState(false);
  const [clickedValues, setClickedValues] = useState<string[]>([]);
  const [sourceOfFundsOtherDescription, setSourceOfFundsOtherDescription] = useState<string>('');
  const handleOptionClick = (key: string): void => {
    if (clickedValues.includes(key)) {
      setClickedValues(clickedValues.filter((item) => item !== key));
    } else {
      setClickedValues((prevState) => ([...prevState, key]));
    }
  };
  const updateSourceOfFundsOnCompleted = (data: { updateUser: { user: { incompleteFields: string[] } } }): void => {
    updateUserState({ ...user, incompleteFields: data.updateUser.user.incompleteFields });
    setLoading(false);
    if (props.onContinue) props.onContinue();
  };
  const [updateSourceOfFunds] = useMutation(UPDATE_USER, {
    variables: {
      input: {
        sourceOfFunds: user.sourceOfFunds,
        sourceOfFundsOtherDescription: clickedValues.includes('OTHER') ? sourceOfFundsOtherDescription : undefined,
      },
    },
    onCompleted: updateSourceOfFundsOnCompleted,
    onError: () => setLoading(false),
  });
  const onContinueClick = (): void => {
    setLoading(true);
    updateSourceOfFunds().then();
  };
  return (
    <OvForm loading={loading} onSubmit={onContinueClick} disableButton={user.sourceOfFunds?.length === 0} submitButton={false}>
      <Typography variant="heading2">{t('user:howToInvest.sourceOfFunds.title')}</Typography>
      <Typography variant="paragraph3">{t('user:howToInvest.sourceOfFunds.subTitle')}</Typography>
      <List className="on-boarding-list">
        {pmamSourceOfFunds.sort((a, b) => {
          if (a.key === 'OTHER') return 1;

          return a.translatedName.localeCompare(b.translatedName);
        }).map((value: {key: string, translatedName: string}) => (
          <GeneralListItem
            text={value.translatedName}
            key={value.key}
            hasSquareCheckbox
            checked={clickedValues.includes(value.key)}
            onClick={() => {
              if (value) {
                handleOptionClick(value.key);
                updateUserState({ ...user, sourceOfFunds: [...clickedValues, value.key] });
              }
            }}
          />
        ))}
        {
          clickedValues.includes('OTHER') ? (
            <SimpleInput
              testId="other-option-description-input"
              label=""
              value={sourceOfFundsOtherDescription}
              style={{ marginBottom: 60 }}
              subtitle=""
              required
              onChange={(event) => setSourceOfFundsOtherDescription(event.target.value)}
              data-testid="other-option-description-input"
              onClear={() => setSourceOfFundsOtherDescription('')}
              inputProps={{
                autoFocus: clickedValues.includes('OTHER'),
              }}
            />
          ) : undefined
        }
      </List>
    </OvForm>
  );
};

SourceOfFunds.defaultProps = {
  onContinue: undefined,
  cached: false,
  title: undefined,
  subTitle: undefined,
  analyticsEvent: undefined,
};

export default SourceOfFunds;
